import { forwardRef } from 'react'

import { cn } from '@/lib/utils'
import type { FxNavItem } from './FxAppRail'
import { FxIcon } from './FxIcon'

/**
 * The phone navigation: Projekte · Übersicht · + · Archiv · Papierkorb along
 * the bottom edge, filled icons in `text-muted`, the current one
 * `primary-strong` with a 3px bar under it, "+" always `primary`.
 *
 * Ported from the design system's `BottomNav`. The 3px bar is drawn as a
 * transparent border on both edges so that an item does not shift by three
 * pixels when it becomes the current one. The bar separating the nav from
 * the page is the reference implementation's 1px rule (`border-divider`), not
 * a shadow: the lift goes the wrong way for something sitting at the bottom.
 * The padding for the phone's home indicator is the one inline style here:
 * `env(safe-area-inset-bottom)` is not a value any token can carry.
 *
 * `FxNavShell` renders it below 576px only.
 */

/** The five destinations, in the order the design draws them. */
export const FX_BOTTOM_NAV_ITEMS: FxNavItem[] = [
  { key: 'projects', icon: 'folder', label: 'Projekte' },
  { key: 'home', icon: 'home', label: 'Übersicht' },
  { key: 'create', icon: 'add', label: 'Neues Musterformular' },
  { key: 'archive', icon: 'archive', label: 'Archiv' },
  { key: 'trash', icon: 'delete', label: 'Papierkorb' },
]

export interface FxBottomNavProps extends React.HTMLAttributes<HTMLElement> {
  active?: string
  items?: FxNavItem[]
  onNavigate?: (key: string) => void
}

export const FxBottomNav = forwardRef<HTMLElement, FxBottomNavProps>(
  (
    { active = 'home', items = FX_BOTTOM_NAV_ITEMS, onNavigate, className, style, ...props },
    ref,
  ) => (
    <nav
      ref={ref}
      aria-label="Hauptnavigation"
      className={cn(
        'flex h-list-row items-stretch justify-around border-t border-divider bg-surface',
        className,
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', ...style }}
      {...props}
    >
      {items.map((item) => {
        const on = active === item.key
        // The create action is never a "you are here": it opens a dialog.
        const accent = item.key === 'create'
        const inner = (
          <FxIcon name={item.icon} size={accent ? 36 : 26} filled={!accent} />
        )
        const shared = {
          'aria-label': item.label,
          'aria-current': on && !accent ? ('page' as const) : undefined,
          className: cn(
            'flex flex-1 items-center justify-center border-y-[3px] border-transparent text-text-muted transition-colors',
            accent && 'text-primary',
            on && !accent && 'border-b-primary-strong text-primary-strong',
          ),
        }
        return item.href ? (
          <a key={item.key} href={item.href} {...shared} onClick={() => onNavigate?.(item.key)}>
            {inner}
          </a>
        ) : (
          <button
            key={item.key}
            type="button"
            {...shared}
            onClick={() => onNavigate?.(item.key)}
          >
            {inner}
          </button>
        )
      })}
    </nav>
  ),
)
FxBottomNav.displayName = 'FxBottomNav'
